import React, { useEffect, useContext, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { ClientContext } from "../context/Client/ClientContext";

import ClientMap from "../components/Client/ClientMap";
import ClientAddress from "../components/Client/ClientAddress";
import SlickSlider from "../components/Client/SlickSlider";
import ClientMessages from "../components/Client/ClientMessages";
import ClientCategories from "../components/Client/ClientCategories";
import ClientKeywords from "../components/Client/ClientKeywords";
import ContactUs from "../components/Client/ContactUs";
import Navbar from "../components/layout/Navbar";
import ClientSocialMediaLinks from "../components/Client/ClientSocialMediaLinks";

const ClientPage = () => {
  const { getClientDetails, getClientImages, getClientKeywords } =
    useContext(ClientContext);

  const [client, setClientDetails] = useState({});
  const [images, setImages] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [logo, setLogo] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);

  const [showMessages, setShowMessages] = useState(true);
  const [showCategories, setShowCategories] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const idBillFoldRootUrl = process.env.REACT_APP_ROOT_IMAGES_PATH || "";

  const getClientInfo = useCallback(async () => {
    try {
      const res = await getClientDetails(id);

      if (!res) {
        return;
      }

      setClientDetails(res);

      const keys = await getClientKeywords(res.clientId);
      setKeywords(keys || []);

      const clientImages = await getClientImages(id);

      if (clientImages && clientImages.length > 0) {
        const logoImage = clientImages.find((img) => img.isLogo === true);

        setImages(clientImages.filter((img) => img !== logoImage));

        if (logoImage) {
          setLogo(logoImage);
        } else {
          setLogo({
            isLogo: true,
            clientId: "defaults",
            imageName: "default-logo.png",
          });
        }
      } else {
        setImages([]);
        setLogo({
          isLogo: true,
          clientId: "defaults",
          imageName: "default-logo.png",
        });
      }
    } catch (error) {
      console.error("Unable to load client details:", error);

      setLogo({
        isLogo: true,
        clientId: "defaults",
        imageName: "default-logo.png",
      });
    }
  }, [id, getClientDetails, getClientImages, getClientKeywords]);

  useEffect(() => {
    getClientInfo();
  }, [getClientInfo]);

  const navBackOne = () => {
    navigate(-1);
  };

  const gotoWebsite = (site) => {
    if (!site) return;

    const cleanSite = site.startsWith("http") ? site : `https://${site}`;
    window.open(cleanSite, "_blank");
  };

  const resetToMessages = () => {
    setShowMessages(true);
    setShowCategories(false);
    setShowKeywords(false);
    setShowMap(false);
    setIsDisabled(false);
  };

  const handleKeywordsBackBtn = () => {
    resetToMessages();
  };

  const handleCatgBackBtn = () => {
    resetToMessages();
  };

  const handleMapBackBtn = () => {
    resetToMessages();
  };

  const showCategoriesBtnHandle = () => {
    setShowMessages(false);
    setShowCategories(true);
    setShowKeywords(false);
    setShowMap(false);
    setIsDisabled(true);
  };

  const showKeysBtnHandle = () => {
    setShowMessages(false);
    setShowCategories(false);
    setShowKeywords(true);
    setShowMap(false);
    setIsDisabled(true);
  };

  const showMapBtnHandle = () => {
    setShowMessages(false);
    setShowCategories(false);
    setShowKeywords(false);
    setShowMap(true);
    setIsDisabled(true);
  };

  const handleGiftcardClick = (merchantId) => {
    if (!merchantId) return;

    const referrer = encodeURIComponent(`${window.location.origin}/home`);

    const path = `https://www.transactall.com/order/?merchantId=${merchantId}&referrer=${referrer}`;

    window.open(path, "_blank");
  };

  const handlePromoBtnClick = (merchantId) => {
    console.log("Promotion clicked", merchantId);
  };

  const activePanelLabel = showCategories
    ? "Attributes"
    : showKeywords
    ? "Keywords"
    : showMap
    ? "Map"
    : "Overview";

  return (
    <div className="gog-page gog-page-spaced gog-client-page">
      {/* ACTION NAVBAR */}
      <Navbar
        client={client}
        handleGiftcardClick={handleGiftcardClick}
        handlePromoBtnClick={handlePromoBtnClick}
      />

      {/* HERO */}
      <section className="gog-client-hero">
        <div className="gog-client-hero-copy">
          <span className="gog-eyebrow">Merchant Profile</span>

          <h1>{client.directoryName || "Merchant Details"}</h1>

          <p>
            View this merchant’s business information, available services,
            contact details, categories, keywords, map location, web store, and
            gift card options.
          </p>
        </div>

        <div className="gog-client-hero-status">
          <span>Current View</span>
          <strong>{activePanelLabel}</strong>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <section className="gog-client-shell">
        {/* LEFT BUSINESS DETAILS */}
        <aside className="gog-client-left-column">
          <div className="gog-side-card gog-client-logo-card">
            <img
              src={`${idBillFoldRootUrl}${logo.clientId || "defaults"}/${
                logo.imageName || "default-logo.png"
              }`}
              alt={`${client.directoryName || "Merchant"} logo`}
            />
          </div>

          <div className="gog-side-card">
            <div className="gog-mini-card-header">
              <span>Location</span>
              <h3>Address</h3>
            </div>

            <ClientAddress client={client} />
          </div>

          <div className="gog-side-card">
            <div className="gog-mini-card-header">
              <span>Contact</span>
              <h3>Contact Details</h3>
            </div>

            <ContactUs client={client} />
          </div>
        </aside>

        {/* CENTER CONTENT */}
        <main className="gog-client-main-column">
          <div className="gog-content-card gog-client-content-card">
            <div className="gog-section-header">
              <span>{activePanelLabel}</span>

              <h2>
                {showCategories
                  ? "Business Attributes"
                  : showKeywords
                  ? "Business Keywords"
                  : showMap
                  ? "Merchant Location"
                  : "Merchant Overview"}
              </h2>

              <p>
                {showCategories
                  ? "Review the categories and attributes connected to this merchant."
                  : showKeywords
                  ? "Review searchable keywords associated with this merchant."
                  : showMap
                  ? "View the merchant location on the map."
                  : "Read the merchant profile information and available details."}
              </p>
            </div>

            <div className="gog-client-tab-body">
              {showMessages && <ClientMessages client={client} />}

              {showCategories && (
                <ClientCategories
                  client={client}
                  handleBackBtn={handleCatgBackBtn}
                />
              )}

              {showKeywords && (
                <ClientKeywords
                  client={client}
                  keywords={keywords}
                  handleBackBtn={handleKeywordsBackBtn}
                />
              )}

              {showMap && <ClientMap client={client} onBack={handleMapBackBtn} />}
            </div>
          </div>

          {images.length > 0 && (
            <div className="gog-client-slider-card">
              <div className="gog-mini-card-header">
                <span>Gallery</span>
                <h3>Merchant Images</h3>
              </div>

              <SlickSlider images={images} />
            </div>
          )}
        </main>

        {/* RIGHT ACTION SIDEBAR */}
        <aside className="gog-client-right-column">
          <div className="gog-side-card">
            <div className="gog-mini-card-header centered">
              <span>Display</span>
              <h3>View More</h3>
            </div>

            <div className="gog-action-stack">
              <button
                type="button"
                className="gog-primary-btn full"
                onClick={showCategoriesBtnHandle}
                disabled={isDisabled}
              >
                Attributes
              </button>

              <button
                type="button"
                className="gog-primary-btn full"
                onClick={showKeysBtnHandle}
                disabled={keywords.length < 1 || isDisabled}
              >
                Keywords
              </button>

              <button
                type="button"
                className="gog-primary-btn full"
                onClick={showMapBtnHandle}
                disabled={isDisabled}
              >
                Map
              </button>
            </div>
          </div>

          <div className="gog-side-card">
            <div className="gog-mini-card-header centered">
              <span>Visit</span>
              <h3>Online Links</h3>
            </div>

            <div className="gog-action-stack">
              <button
                type="button"
                disabled={!client.webSite || isDisabled}
                onClick={() => gotoWebsite(client.webSite)}
                className="gog-secondary-btn full"
              >
                Web Site
              </button>

              <button
                type="button"
                disabled={!client.webStore || isDisabled}
                onClick={() => gotoWebsite(client.webStore)}
                className="gog-secondary-btn full"
              >
                Web Store
              </button>
            </div>
          </div>

          <div className="gog-side-card">
            <div className="gog-mini-card-header centered">
              <span>Social</span>
              <h3>Connect</h3>
            </div>

            <ClientSocialMediaLinks
              client={client}
              gotoWebsite={gotoWebsite}
              isDisabled={isDisabled}
            />
          </div>

          <button
            type="button"
            onClick={navBackOne}
            disabled={isDisabled}
            className="gog-light-btn full"
          >
            Back
          </button>
        </aside>
      </section>
    </div>
  );
};

export default ClientPage;